import { Box, Button } from '@mui/material';
import { AxiosError } from 'axios';
import { useRootContext } from 'components/RootContainer';
import { FC } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { REPORT_PATH } from '../../../../../../@constants/Navigation/AppPaths/RootPaths';
import EmptyContent from '../../../../../../components/EmptyContent';
import ModalLoading from '../../../../../../components/ModalLoading';
import { ArrowLeft2Icon } from '../../../../../../components/SvgIcons/ArrowLeft2Icon';
import { Bookmark2Icon } from '../../../../../../components/SvgIcons/Bookmark2Icon';
import { useFeedback } from '../../../../../../hooks/useFeedback';
import { ExportErrorFeedback } from '../../../../helpers/ExportErrorFeedback';
import { TrialBalanceReportRequestModel } from './api/models/TrialBalanceReportRequestModel';
import { FilterFormProvider } from './components/FilterForm/FilterFormProvider';
import { getDefaultValues } from './components/FilterForm/defaultValues';
import { dataFormToRequest } from './utils/dataFormToRequest';

const TrialBalanceReportPage: FC = () => {
  const {
    globalSettings,
    HotelApi: {
      report: {
        financialReports: { trialBalanceReport: TrialBalanceService },
      },
    },
  } = useRootContext();

  const { onError, closeSnackbar } = useFeedback();

  const exportPDF = useMutation({
    mutationKey: ['exportPDF'],
    mutationFn: (payload: TrialBalanceReportRequestModel) => {
      return TrialBalanceService.exportPDF({
        ...payload,
      });
    },
    onError: (error: AxiosError, variables) => {
      const content = (
        <ExportErrorFeedback
          status={error.response?.status}
          message={(error as { message?: string }).message}
          onExportCsv={() => exportPDF.mutate({ ...variables, file_type: 'excel' })}
          onClose={closeSnackbar}
          recommendedFormat="excel"
        />
      );

      onError({
        snackbar: {
          title: 'Error exporting report',
          message: content,
        },
        options: { persist: false },
        autoHideDuration: 5000,
      });
    },
  });

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(REPORT_PATH);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
        }}
      >
        <Box
          sx={{
            width: '24rem',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'colors.gray.o10',
          }}
        >
          <Button
            startIcon={<ArrowLeft2Icon />}
            sx={{
              fontSize: '1rem',
              fontWeight: 'fontWeightMedium',
              justifyContent: 'flex-start',
              m: '1rem',
            }}
            onClick={handleBackClick}
          >
            Back
          </Button>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <FilterFormProvider
              defaultValues={getDefaultValues(globalSettings!)}
              onSubmit={data => {
                return exportPDF.mutate(dataFormToRequest(data));
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, position: 'relative' }}>
          <EmptyContent IconProps={<Bookmark2Icon />} title="No report selected" />
          <ModalLoading open={exportPDF.isLoading} title="Exporting..." />
        </Box>
      </Box>
    </>
  );
};

export default TrialBalanceReportPage;
